// src/utils/sowConstants.js (FINAL, COMPLETE VERSION)
export const MONITORING_SCOPE = {
  standard: [
      "• Basic infrastructure monitoring during business hours (8x5)",
      "• Monthly performance reports",
      "• Basic security monitoring and alerts",
      "• Automated backup management",
      "• Incident response during business hours"
  ],
  professional: [
      "• Enhanced infrastructure monitoring (16x5)",
      "• Weekly performance reports",
      "• Advanced security monitoring and alerts",
      "• Automated backup management with quick recovery",
      "• Priority incident response",
      "• Quarterly architecture reviews",
      "• Enhanced compliance monitoring"
  ],
  enterprise: [
      "• 24/7 comprehensive infrastructure monitoring",
      "• Real-time performance dashboard",
      "• Advanced security monitoring with threat detection",
      "• Automated backup management with instant recovery",
      "• 15-minute response SLA for critical issues",
      "• Monthly architecture reviews",
      "• Dedicated technical account manager",
      "• Advanced compliance and security auditing"
  ]
};

// Now a function that takes state
export const getProjectTimeline = (state) => ([
  { phase: "Project Initiation", duration: "Week 1", details: "Project kickoff, requirement validation, and access setup" },
  { phase: "Infrastructure Setup", duration: "Week 1-2", details: "AWS environment configuration and security setup" },
  { phase: "Network Configuration", duration: "Week 2", details: `Setup of ${state.siteCount} site connections and security groups` },
  state.uatServer && { phase: "UAT Environment", duration: "Week 2-3", details: "UAT environment setup and testing" },
  state.drServer && { phase: "DR Environment", duration: "Week 3", details: "DR environment setup and replication configuration" },
  { phase: "Security Implementation", duration: "Week 3-4", details: "Security measures, monitoring, and compliance setup" },
  { phase: "Testing & Validation", duration: "Week 4-5", details: "Comprehensive testing of all components" },
  { phase: "Production Migration", duration: "Week 5-6", details: "Production environment validation and handover" }
].filter(Boolean)); // Remove empty objects caused by conditions

export const SLA_DETAILS = {
  standard: {
      responseTime: "4 business hours",
      availability: "99.5%",
      monitoring: "Business hours (8x5)"
  },
  professional: {
      responseTime: "2 business hours",
      availability: "99.9%",
      monitoring: "Extended hours (16x5)"
  },
  enterprise: {
      responseTime: "15 minutes",
      availability: "99.95%",
      monitoring: "24x7"
  }
};

export const PROJECT_DELIVERABLES = [
  "• Fully configured AWS infrastructure",
  "• Comprehensive infrastructure documentation",
  "• Monitoring and alerting system",
  "• Backup and recovery procedures",
  "• Security configurations and compliance documentation",
  "• Standard operating procedures",
  "• Knowledge transfer sessions"
];

// Now a function that takes state
export const getProjectAssumptions = (state) => ([
  "• Client has or will create required AWS accounts",
  "• Suitable network connectivity exists between sites",
  "• Client will provide timely access to required resources and personnel",
  "• Applications are compatible with AWS infrastructure",
  `• Initial contract term of ${state.contractLength} months`,
  "• All required third-party software licenses will be provided by the client",
  "• Client maintains responsibility for application-level management"
]);

export const PROJECT_EXCLUSIONS = [
  "• Application development and maintenance",
  "• Database administration and optimization",
  "• End-user support and training",
  "• Third-party software licensing",
  "• Content creation and management",
  "• Domain registration and DNS management",
  "• On-premises infrastructure management",
  "• Data migration services (available as separate service)"
];

export const CLIENT_RESPONSIBILITIES = [
  "• Provide timely access to AWS accounts and required resources",
  "• Assign dedicated technical point of contact",
  "• Review and approve infrastructure designs",
  "• Participate in scheduled review meetings",
  "• Maintain application-level security",
  "• Provide timely responses to inquiries and approval requests",
  "• Maintain DNS and domain configurations",
  "• Ensure compliance with AWS acceptable use policies"
];

export const QUALITY_ASSURANCE = [
  "• Infrastructure deployment validation",
  "• Security compliance verification",
  "• Performance baseline establishment",
  "• High availability testing",
  "• Disaster recovery testing and validation",
  "• Monitoring system verification",
  "• Alert system validation",
  "• Documentation review and validation"
];

// Now a function that takes state and supportTiers
export const getCommunicationPlan = (state, supportTiers) => ([
  `• ${supportTiers[state.supportTier].name} support channel access`,
  "• Weekly progress reports during implementation",
  "• Monthly service review meetings",
  "• Quarterly business reviews",
  "• Access to online support portal",
  "• Scheduled maintenance notifications",
  "• Incident response communications"
]);

// Now a function that takes state
export const getImplementationPhases = (state) => ([
  {
      phase: "Phase 1: Project Initiation",
      tasks: [
          "• Project kickoff meeting",
          "• Requirements validation",
          "• Access setup and verification",
          "• Project plan finalization"
      ].join('\n')
  },
  {
      phase: "Phase 2: Infrastructure Setup",
      tasks: [
          "• AWS environment configuration",
          "• Security group setup",
          "• VPC configuration",
          "• Storage provisioning"
      ].join('\n')
  },
  {
      phase: "Phase 3: Network Configuration",
      tasks: [
          `• ${state.siteCount} site connection setup`,
          state.centralizedVpn && "• VPN hub implementation",
          state.requiresNlb && "• Network Load Balancer configuration",
          state.requiresAlb && "• Application Load Balancer setup"
      ].filter(Boolean).join('\n')
  },
  {
      phase: "Phase 4: Security Implementation",
      tasks: [
          "• Security controls implementation",
          "• Monitoring setup",
          "• Backup configuration",
          "• Compliance validation"
      ].join('\n')
  },
  {
      phase: "Phase 5: Testing & Validation",
      tasks: [
          "• Infrastructure testing",
          "• Security validation",
          "• Performance testing",
          "• Monitoring verification"
      ].join('\n')
  },
  {
      phase: "Phase 6: Production Handover",
      tasks: [
          "• Final validation",
          "• Documentation handover",
          "• Knowledge transfer",
          "• Production support initiation"
      ].join('\n')
  }
]);

// Now a function that takes state and slaDetails
export const getSupportProcedures = (state, slaDetails) => ([
  "Incident Management",
  `• Response Time: ${slaDetails.responseTime}`,
  `• Availability: ${SLA_DETAILS[state.supportTier].availability}`,
  `• Support Hours: ${SLA_DETAILS[state.supportTier].monitoring}`,
  "",
  "Escalation Path",
  "• Level 1: 24/7 Support Team",
  "• Level 2: Technical Operations Team",
  "• Level 3: Senior Engineering Team",
  "• Level 4: Service Delivery Manager"
]);

// Now a function that takes state and slaDetails
export const getTermsAndConditions = (state, slaDetails) => ([
  `• Initial Term: ${state.contractLength} months`,
  "• Auto-renewal: 12-month terms",
  "• Early Termination: 90 days notice required",
  "• Payment Terms: Monthly in advance",
  `• Service Credits: Available for availability below ${slaDetails.availability}`,
  "• Change Management: Follows AWS best practices"
]);

export const formatSLA = (supportTier, slaDetails, isPaperless) => {
const slaTemplates = {
  standard: isPaperless
  ? `Our Standard Support (24x7) Service Level Agreement includes:

• Response Times
  - Critical Issues: ${slaDetails.responseTime}
  - High Priority: 8 business hours
  - Normal Priority: Next business day

• Service Availability: ${slaDetails.availability}
• Monitoring Coverage: ${slaDetails.monitoring}

Support Channel Access:
• Email support during business hours
• Online ticket system
• Phone support for critical issues
• Monthly service reports

Incident Management:
• Basic incident tracking and reporting
• Business hours response team
• Regular status updates
• Post-incident review for critical issues`

: `Our Standard Support (8x5) Service Level Agreement includes:

• Response Times
- Critical Issues: ${slaDetails.responseTime}
- High Priority: 8 business hours
- Normal Priority: Next business day

• Service Availability: ${slaDetails.availability}
• Monitoring Coverage: ${slaDetails.monitoring}

Support Channel Access:
• Email support during business hours
• Online ticket system
• Phone support for critical issues
• Monthly service reports

Incident Management:
• Basic incident tracking and reporting
• Business hours response team
• Regular status updates
• Post-incident review for critical issues`,

    professional: isPaperless ?
    `Our Professional Support (24x7) Service Level Agreement includes:

    • Response Times
      - Critical Issues: ${slaDetails.responseTime}
      - High Priority: 4 business hours
      - Normal Priority: 8 business hours
    
    • Service Availability: ${slaDetails.availability}
    • Monitoring Coverage: ${slaDetails.monitoring}
    
    Support Channel Access:
    • Priority email support
    • Advanced online ticket system
    • Direct phone support
    • Weekly service reports
    • Quarterly review meetings
    
    Incident Management:
    • Advanced incident tracking and reporting
    • Extended hours response team
    • Proactive issue identification
    • Detailed post-incident analysis
    • Root cause analysis reports` :
    
    `Our Professional Support (16x5) Service Level Agreement includes:

• Response Times
- Critical Issues: ${slaDetails.responseTime}
- High Priority: 4 business hours
- Normal Priority: 8 business hours

• Service Availability: ${slaDetails.availability}
• Monitoring Coverage: ${slaDetails.monitoring}

Support Channel Access:
• Priority email support
• Advanced online ticket system
• Direct phone support
• Weekly service reports
• Quarterly review meetings

Incident Management:
• Advanced incident tracking and reporting
• Extended hours response team
• Proactive issue identification
• Detailed post-incident analysis
• Root cause analysis reports`,

    enterprise: `Our Enterprise Support (24x7) Service Level Agreement includes:

• Response Times
- Critical Issues: ${slaDetails.responseTime}
- High Priority: 1 hour
- Normal Priority: 4 hours

• Service Availability: ${slaDetails.availability}
• Monitoring Coverage: ${slaDetails.monitoring}

Support Channel Access:
• Premium 24x7 support
• Dedicated support line
• Priority ticket handling
• Real-time dashboard access
• Monthly executive reviews
• Dedicated technical account manager

Incident Management:
• Enterprise-grade incident management
• 24x7 response team
• Proactive issue prevention
• Comprehensive post-incident analysis
• Continuous service improvement
• Priority escalation path`
};

return slaTemplates[supportTier];
};
